import React from 'react';
import 'mana-font';
import { spacing } from '../../styles/tokens';

interface ManaSymbolProps {
  manaCost: string | null | undefined;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Renders MTG mana symbols from a mana cost string like "{1}{W}{U}"
 * Uses the mana-font library for authentic MTG symbol rendering
 */
export const ManaSymbol: React.FC<ManaSymbolProps> = ({ manaCost, size = 'md' }) => {
  if (!manaCost) return null;

  // Parse the mana cost string into individual symbols
  // Format is like: {1}{W}{U}{B}{R}{G} where each {} is one symbol
  const symbolRegex = /\{([^}]+)\}/g;
  const matches = Array.from(manaCost.matchAll(symbolRegex));

  if (matches.length === 0) return null;

  // Map mana values to mana-font class names
  const getManaClass = (symbol: string): string => {
    const symbolMap: { [key: string]: string } = {
      // Numbers
      '0': 'ms-0',
      '1': 'ms-1',
      '2': 'ms-2',
      '3': 'ms-3',
      '4': 'ms-4',
      '5': 'ms-5',
      '6': 'ms-6',
      '7': 'ms-7',
      '8': 'ms-8',
      '9': 'ms-9',
      '10': 'ms-10',
      '11': 'ms-11',
      '12': 'ms-12',
      '13': 'ms-13',
      '14': 'ms-14',
      '15': 'ms-15',
      '16': 'ms-16',
      '17': 'ms-17',
      '18': 'ms-18',
      '19': 'ms-19',
      '20': 'ms-20',
      // Colors
      'W': 'ms-w',
      'U': 'ms-u',
      'B': 'ms-b',
      'R': 'ms-r',
      'G': 'ms-g',
      // Multi-color
      'WU': 'ms-wu',
      'UB': 'ms-ub',
      'BR': 'ms-br',
      'RG': 'ms-rg',
      'GW': 'ms-gw',
      'WB': 'ms-wb',
      'UR': 'ms-ur',
      'BG': 'ms-bg',
      'RW': 'ms-rw',
      'GU': 'ms-gu',
      // Phyrexian
      'WP': 'ms-wp',
      'UP': 'ms-up',
      'BP': 'ms-bp',
      'RP': 'ms-rp',
      'GP': 'ms-gp',
      'P': 'ms-p',
      // Special
      'X': 'ms-x',
      'Y': 'ms-y',
      'Z': 'ms-z',
      'S': 'ms-s',
      'C': 'ms-c',
      'E': 'ms-e',
    };

    return symbolMap[symbol.toUpperCase()] || 'ms-0';
  };

  const sizeMap = {
    sm: { fontSize: '16px', marginRight: spacing.xs },
    md: { fontSize: '20px', marginRight: spacing.sm },
    lg: { fontSize: '28px', marginRight: spacing.md },
  };

  const symbolSize = sizeMap[size];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexWrap: 'wrap' }}>
      {matches.map((match, index) => {
        const symbol = match[1];
        const manaClass = getManaClass(symbol);

        return (
          <i
            key={index}
            className={`ms ${manaClass}`}
            style={{
              fontSize: symbolSize.fontSize,
              display: 'inline-block',
              lineHeight: '1',
            }}
            title={symbol}
          />
        );
      })}
    </div>
  );
};
